/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Target, AlertTriangle, Sparkles, Loader2, Clock, Check, X, ChevronRight,
  TrendingUp, Building2, User,
} from "lucide-react";

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

// ====================================================================
// MISSION CARD — Active
// ====================================================================
const MissionCard = ({ mission, onSynthesize, onComplete, onCancel, onOpenAgency }) => {
  const [synthLoading, setSynthLoading] = useState(false);
  const [synth, setSynth] = useState(mission.last_synthesis);

  const doSynth = async () => {
    setSynthLoading(true);
    try {
      const { data } = await onSynthesize(mission.id);
      setSynth(data.synthesis);
    } finally { setSynthLoading(false); }
  };

  const pct = mission.progression_pct || 0;
  const progressColor = pct >= 60 ? "from-emerald-500 to-emerald-400"
    : pct >= 30 ? "from-amber-500 to-amber-400"
    : "from-red-600 to-red-400";

  return (
    <Card className={`p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 ${mission.at_risk ? "border-red-500/30" : "border-[#A855F7]/15"}`} data-testid={`mission-${mission.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#C084FC] mb-1" style={MONO}>
            <Target className="w-3 h-3" />MISSION ACTIVE · J+{mission.duration_days - mission.days_remaining}/{mission.duration_days}
            {mission.at_risk && <span className="ml-2 px-2 py-0.5 rounded bg-red-500/15 text-red-400 text-[9px]">À RISQUE</span>}
          </div>
          <button onClick={() => onOpenAgency(mission.agency_id)} className="text-lg text-white hover:text-[#C084FC] transition-colors text-left" style={DISPLAY} data-testid={`mission-agency-${mission.id}`}>
            {mission.agency_name} <span className="text-white/30">→</span>
          </button>
          <div className="text-xs text-white/55 mt-1 flex items-center gap-3">
            <span className="flex items-center gap-1"><User className="w-3 h-3" />{mission.director_name || "directeur non désigné"}</span>
            <span className="flex items-center gap-1" style={MONO}><Clock className="w-3 h-3" />{mission.days_remaining}j restants</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-white/40 uppercase" style={MONO}>Score</div>
          <div className="text-2xl text-white" style={DISPLAY}>{mission.current_score} <span className="text-xs text-white/40">/ {mission.target_score}</span></div>
          <div className="text-[10px] text-white/40" style={MONO}>début : {mission.initial_metrics.score}</div>
        </div>
      </div>

      {/* Objectif principal */}
      <div className="mb-3 p-3 rounded-md bg-[#A855F7]/8 border border-[#A855F7]/20">
        <div className="text-[10px] uppercase tracking-widest text-[#C084FC] mb-1" style={MONO}>Objectif principal</div>
        <div className="text-sm text-white/90">{mission.objective_main}</div>
      </div>

      {/* Sous-objectifs */}
      {mission.sub_objectives?.length > 0 && (
        <div className="mb-3 space-y-1">
          {mission.sub_objectives.map((so, i) => (
            <div key={i} className="text-xs text-white/65 flex items-start gap-2">
              <ChevronRight className="w-3 h-3 text-white/30 mt-0.5 flex-shrink-0" />{so}
            </div>
          ))}
        </div>
      )}

      {/* Barre de progression */}
      <div className="mb-3">
        <div className="flex justify-between text-[10px] mb-1.5" style={MONO}>
          <span className="text-white/40">PROGRESSION</span>
          <span className="text-white/75">{pct}%</span>
        </div>
        <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
          <div className={`h-full bg-gradient-to-r ${progressColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Dernière synthèse */}
      {synth && (
        <div className="mb-3 p-3 rounded-md bg-gradient-to-br from-purple-950/30 to-[#0c0814]/80 border border-purple-500/20">
          <div className="text-[10px] uppercase tracking-widest text-purple-300 mb-1.5 flex items-center gap-1.5" style={MONO}><Sparkles className="w-3 h-3" />Dernière synthèse Providence</div>
          <div className="text-xs text-white/85 whitespace-pre-wrap leading-relaxed">{synth}</div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
        <Button size="sm" onClick={doSynth} disabled={synthLoading} className="bg-gradient-to-r from-[#A855F7] to-[#C084FC] hover:opacity-90 text-white text-xs h-8" data-testid={`synth-${mission.id}`}>
          {synthLoading ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Providence…</> : <><Sparkles className="w-3 h-3 mr-1.5" />Synthèse Providence</>}
        </Button>
        <Button size="sm" variant="outline" onClick={() => onComplete(mission.id)} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-8" data-testid={`complete-${mission.id}`}>
          <Check className="w-3 h-3 mr-1.5" />Mission terminée
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onCancel(mission.id)} className="text-white/55 hover:text-red-400 text-xs h-8" data-testid={`cancel-${mission.id}`}>
          <X className="w-3 h-3 mr-1.5" />Annuler
        </Button>
      </div>
    </Card>
  );
};

// ====================================================================
// CANDIDATE CARD — Mission proposée
// ====================================================================
const CandidateCard = ({ candidate, onLaunch, defaults }) => {
  const [open, setOpen] = useState(false);
  const [obj, setObj] = useState("");
  const [director, setDirector] = useState(candidate.director || "");
  const [duration, setDuration] = useState(defaults?.default_duration_days || 30);
  const [creating, setCreating] = useState(false);

  const launch = async () => {
    setCreating(true);
    try {
      await onLaunch(candidate.agency_id, {
        director_name: director,
        objective_main: obj || undefined,
        duration_days: duration,
      });
      setOpen(false);
    } finally { setCreating(false); }
  };

  return (
    <Card className="p-4 bg-gradient-to-br from-amber-950/15 to-[#0c0814]/80 border-amber-500/20" data-testid={`candidate-${candidate.agency_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1 flex items-center gap-1.5" style={MONO}>
            <AlertTriangle className="w-3 h-3" />CANDIDAT MISSION
          </div>
          <div className="text-base text-white" style={DISPLAY}>{candidate.agency_name}</div>
          <div className="text-xs text-white/55">{candidate.city} · {candidate.director}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-white/40 uppercase" style={MONO}>Criticité</div>
          <div className="text-xl text-red-400" style={DISPLAY}>{candidate.criticality}</div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="text-center p-2 rounded bg-white/[0.02]">
          <div className="text-[9px] text-white/40 uppercase" style={MONO}>Score</div>
          <div className="text-sm text-red-400 font-bold" style={DISPLAY}>{candidate.score}</div>
        </div>
        <div className="text-center p-2 rounded bg-white/[0.02]">
          <div className="text-[9px] text-white/40 uppercase" style={MONO}>Tend.</div>
          <div className="text-sm text-red-400 font-bold" style={DISPLAY}>{candidate.trend}%</div>
        </div>
        <div className="text-center p-2 rounded bg-white/[0.02]">
          <div className="text-[9px] text-white/40 uppercase" style={MONO}>Process</div>
          <div className="text-sm text-white/85" style={DISPLAY}>{candidate.process_score}%</div>
        </div>
        <div className="text-center p-2 rounded bg-white/[0.02]">
          <div className="text-[9px] text-white/40 uppercase" style={MONO}>Satis.</div>
          <div className="text-sm text-white/85" style={DISPLAY}>{candidate.satisfaction_rating}/5</div>
        </div>
      </div>

      {!open ? (
        <Button onClick={() => setOpen(true)} size="sm" className="w-full bg-gradient-to-r from-[#A855F7] to-[#C084FC] hover:opacity-90 text-white h-9" data-testid={`launch-${candidate.agency_id}`}>
          <Sparkles className="w-3.5 h-3.5 mr-2" />Lancer une mission Providence
        </Button>
      ) : (
        <div className="space-y-3 pt-3 border-t border-white/5">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/55 mb-1 block" style={MONO}>Directeur en charge</label>
            <Input value={director} onChange={(e) => setDirector(e.target.value)} placeholder="ex. Sophie Bernard" className="bg-white/5 border-white/10 text-sm" data-testid={`director-${candidate.agency_id}`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/55 mb-1 block" style={MONO}>
              Objectif principal <span className="text-white/30 normal-case">(facultatif — Providence en propose un si vide)</span>
            </label>
            <Input value={obj} onChange={(e) => setObj(e.target.value)} placeholder="ex. Remonter le score à 70/100 d'ici 30 jours" className="bg-white/5 border-white/10 text-sm" data-testid={`obj-${candidate.agency_id}`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/55 mb-1 block" style={MONO}>Durée (jours)</label>
            <Input type="number" value={duration} onChange={(e) => setDuration(parseInt(e.target.value) || 30)} min="7" max="180" className="bg-white/5 border-white/10 text-sm" />
          </div>
          <div className="flex gap-2">
            <Button onClick={launch} disabled={creating || !director} size="sm" className="flex-1 bg-gradient-to-r from-[#A855F7] to-[#C084FC] hover:opacity-90 text-white" data-testid={`confirm-${candidate.agency_id}`}>
              {creating ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Création…</> : "Confirmer"}
            </Button>
            <Button onClick={() => setOpen(false)} size="sm" variant="ghost" className="text-white/55">Annuler</Button>
          </div>
        </div>
      )}
    </Card>
  );
};

// ====================================================================
// MISSIONS VIEW (main)
// ====================================================================
export const MissionsView = ({ groupId, onOpenAgency }) => {
  const [candidates, setCandidates] = useState(null);
  const [missions, setMissions] = useState(null);
  const [completed, setCompleted] = useState(null);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState("active");
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    Promise.all([
      api.get(`/groups/${groupId}/missions/candidates`).then((r) => setCandidates(r.data)),
      api.get(`/groups/${groupId}/missions?status=active`).then((r) => setMissions(r.data)),
      api.get(`/groups/${groupId}/missions?status=completed&limit=10`).then((r) => setCompleted(r.data)),
      api.get(`/groups/${groupId}/missions-settings`).then((r) => setSettings(r.data)),
    ]).catch(() => {});
  }, [groupId, reloadKey]);

  const launch = async (agency_id, body) => {
    await api.post(`/groups/${groupId}/missions`, { agency_id, ...body });
    reload();
  };

  const synthesize = async (mid) => {
    const { data } = await api.post(`/groups/${groupId}/missions/${mid}/synthesize`);
    reload();
    return { data };
  };

  const complete = async (mid) => {
    if (!window.confirm("Marquer cette mission comme terminée ?")) return;
    await api.patch(`/groups/${groupId}/missions/${mid}`, { status: "completed" });
    reload();
  };

  const cancel = async (mid) => {
    if (!window.confirm("Annuler cette mission ? Elle restera consultable dans l'historique.")) return;
    await api.patch(`/groups/${groupId}/missions/${mid}`, { status: "cancelled" });
    reload();
  };

  if (!candidates || !missions) return <Skeleton className="h-96 bg-[#0c0814]/80" />;

  const activeCount = missions.total;
  const candidatesCount = candidates.n_total;

  return (
    <div className="space-y-6" data-testid="missions-view">
      <div>
        <h2 className="text-xl text-white tracking-tight" style={DISPLAY}>Missions Providence</h2>
        <p className="text-sm text-white/55 mt-1">
          Cycles courts de pilotage agence par agence. <span className="text-white/85">Vous validez chaque mission</span> et désignez un directeur en charge.
          Providence détecte les signaux faibles et synthétise l'avancement.
        </p>
      </div>

      {/* KPI summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card className="p-4 bg-gradient-to-br from-amber-950/15 to-[#0c0814]/80 border-amber-500/20">
          <div className="text-[10px] uppercase tracking-widest text-amber-400" style={MONO}>Candidats détectés</div>
          <div className="text-3xl text-white mt-1" style={DISPLAY}>{candidatesCount}</div>
          <div className="text-[10px] text-white/40 mt-1">seuil : score &lt; {candidates.thresholds.score_threshold} ET tendance &lt; {candidates.thresholds.trend_threshold}%</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-[#0c0814]/90 to-[#1a0f2e]/80 border-[#A855F7]/20">
          <div className="text-[10px] uppercase tracking-widest text-[#C084FC]" style={MONO}>Missions actives</div>
          <div className="text-3xl text-white mt-1" style={DISPLAY}>{activeCount}</div>
          <div className="text-[10px] text-white/40 mt-1">en cours de pilotage</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-emerald-950/15 to-[#0c0814]/80 border-emerald-500/20">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400" style={MONO}>Complétées</div>
          <div className="text-3xl text-white mt-1" style={DISPLAY}>{completed?.total || 0}</div>
          <div className="text-[10px] text-white/40 mt-1">historique du groupe</div>
        </Card>
      </div>

      {/* CANDIDATS */}
      {candidatesCount > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base text-white" style={DISPLAY}>
              <span className="text-amber-400">●</span> Candidats — Providence propose
            </h3>
            <div className="text-xs text-white/40">{candidatesCount} agence(s) éligible(s)</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {candidates.candidates.map((c) => (
              <CandidateCard key={c.agency_id} candidate={c} onLaunch={launch} defaults={settings} />
            ))}
          </div>
        </div>
      )}

      {/* MISSIONS ACTIVES — filtre */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base text-white" style={DISPLAY}>
            <span className="text-[#C084FC]">●</span> Vos missions
          </h3>
          <div className="flex gap-1 text-xs" style={MONO}>
            {[
              { v: "active", label: "ACTIVES", n: activeCount },
              { v: "completed", label: "TERMINÉES", n: completed?.total || 0 },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                className={`px-3 py-1.5 rounded-md uppercase tracking-widest transition-colors ${filter === f.v ? "bg-[#A855F7]/15 text-[#C084FC] border border-[#A855F7]/30" : "text-white/40 hover:text-white/75"}`}
                data-testid={`filter-${f.v}`}
              >
                {f.label} · {f.n}
              </button>
            ))}
          </div>
        </div>

        {filter === "active" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {missions.items.length === 0 && (
              <Card className="md:col-span-2 p-10 bg-[#0c0814]/80 border-white/5 text-center">
                <div className="text-white/40 text-sm">Aucune mission active.</div>
                {candidatesCount > 0 && <div className="text-xs text-white/30 mt-2">Lancez une mission depuis la section "Candidats" ci-dessus.</div>}
              </Card>
            )}
            {missions.items.map((m) => (
              <MissionCard key={m.id} mission={m} onSynthesize={synthesize} onComplete={complete} onCancel={cancel} onOpenAgency={onOpenAgency} />
            ))}
          </div>
        )}

        {filter === "completed" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(completed?.items || []).length === 0 && (
              <Card className="md:col-span-2 p-10 bg-[#0c0814]/80 border-white/5 text-center text-white/40">Aucune mission terminée.</Card>
            )}
            {(completed?.items || []).map((m) => (
              <Card key={m.id} className="p-4 bg-[#0c0814]/80 border-white/5 opacity-80" data-testid={`completed-${m.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1" style={MONO}>{m.status === "completed" ? "TERMINÉE" : "ANNULÉE"} · {new Date(m.completed_at || m.updated_at).toLocaleDateString("fr-FR")}</div>
                <div className="text-base text-white" style={DISPLAY}>{m.agency_name}</div>
                <div className="text-xs text-white/55 mt-1">{m.objective_main}</div>
                <div className="text-xs text-white/40 mt-2" style={MONO}>Score : {m.initial_metrics?.score} → {m.current_score || "—"}</div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ====================================================================
// RADAR WIDGET — placé en Vue Groupe
// ====================================================================
export const RadarWidget = ({ groupId, onOpenAgency, onGoMissions }) => {
  const [radar, setRadar] = useState(null);

  useEffect(() => {
    api.get(`/groups/${groupId}/radar`).then((r) => setRadar(r.data)).catch(() => {});
  }, [groupId]);

  if (!radar) return null;

  return (
    <Card className="p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#1a0f2e]/80 border-[#A855F7]/15" data-testid="radar-widget">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium text-white flex items-center gap-2" style={DISPLAY}>
          <Target className="w-4 h-4 text-[#C084FC]" />Vos agences sous votre radar
        </div>
        <button onClick={onGoMissions} className="text-[11px] text-[#C084FC] hover:text-white transition-colors" style={MONO}>
          MISSIONS →
        </button>
      </div>
      <p className="text-xs text-white/40 mb-4">Sur 30 jours · plus vous interagissez, plus une agence est "sous radar". Aide à équilibrer le temps managérial.</p>

      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { k: "n_intense", label: "Intense", color: "text-emerald-400" },
          { k: "n_regulier", label: "Régulier", color: "text-sky-400" },
          { k: "n_occasionnel", label: "Occasionnel", color: "text-amber-400" },
          { k: "n_hors_radar", label: "Hors radar", color: "text-red-400" },
        ].map((b) => (
          <div key={b.k} className="text-center p-2 rounded bg-white/[0.02]">
            <div className={`text-2xl font-bold ${b.color}`} style={DISPLAY}>{radar[b.k]}</div>
            <div className="text-[9px] text-white/40 uppercase tracking-widest mt-0.5" style={MONO}>{b.label}</div>
          </div>
        ))}
      </div>

      {radar.hors_radar.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-red-400 mb-2 flex items-center gap-1.5" style={MONO}>
            <AlertTriangle className="w-3 h-3" />Hors radar — agences à reprendre en main
          </div>
          <div className="space-y-1.5">
            {radar.hors_radar.slice(0, 4).map((r) => (
              <button
                key={r.agency_id}
                onClick={() => onOpenAgency(r.agency_id)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-white/[0.02] hover:bg-red-500/10 border border-white/5 hover:border-red-500/20 text-xs transition-all"
                data-testid={`hors-radar-${r.agency_id}`}
              >
                <span className="flex items-center gap-2 text-white/85">
                  <Building2 className="w-3 h-3 opacity-50" />{r.agency_name}
                </span>
                <span className="text-white/40" style={MONO}>0 action sur 30j</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
